import { useParams } from 'react-router-dom';

import { useLeaderboardQuery } from '@/services/endpoints/leaderboard';
import { useAppSelector } from '@/store/hooks';
import { useGameSelection } from '@/hooks/useGameSelection';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { Loader } from '@/components/common/Loader';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import type { LeaderboardEntry } from '@/types';

export function LeaderboardDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const seasonId = Number(id);
  const { activeGame } = useGameSelection();
  const meFromStore = useAppSelector((s) => s.auth.user);
  const meQuery = useGetMeQuery(undefined, { skip: !!meFromStore });
  const me = meFromStore ?? meQuery.data ?? null;
  const myId = me?.id;

  const { data: entries, isLoading, isError } = useLeaderboardQuery(
    {
      season_id: seasonId,
      ...(activeGame ? { game_id: activeGame.id } : {}),
      limit: 200,
    },
    { skip: !Number.isFinite(seasonId) || seasonId <= 0 },
  );

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">Season ranking</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {activeGame ? activeGame.name : 'All games'}
        </p>
      </header>

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader />
        </div>
      )}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          Failed to load this leaderboard.
        </div>
      )}

      {!isLoading && !isError && (!entries || entries.length === 0) && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          No entries yet — be the first to compete in this season.
        </div>
      )}

      {entries && entries.length > 0 && (
        <ul className="flex flex-col gap-2">
          {entries.map((entry, index) => (
            <li key={`${entry.user?.id ?? 'team'}-${index}`}>
              <Row entry={entry} index={index} highlight={!!myId && entry.user?.id === myId} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({
  entry,
  index,
  highlight,
}: {
  entry: LeaderboardEntry;
  index: number;
  highlight?: boolean;
}) {
  const position = entry.leaderboard_position ?? index + 1;
  const name = entry.team?.name ?? entry.user?.nickname ?? 'Player';
  const avatar = entry.team?.logo ?? entry.user?.avatar;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border bg-bg-secondary px-3 py-2',
        highlight ? 'border-brand' : 'border-transparent',
      )}
    >
      <span
        className={cn(
          'w-6 shrink-0 text-center text-xs font-bold',
          position === 1 ? 'text-brand' : 'text-fg-muted',
        )}
      >
        {position}
      </span>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary text-xs font-bold text-fg">
        {avatar ? (
          <img
            src={cdnImage(avatar)}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <span aria-hidden>{name.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{name}</span>
      <span
        className={cn(
          'shrink-0 text-sm font-bold tabular-nums',
          position === 1 ? 'text-brand' : 'text-fg',
        )}
      >
        {entry.points}
      </span>
    </div>
  );
}
